import { ChangeDetectorRef, Component, type OnDestroy, type OnInit } from '@angular/core'
import { MatDialog } from '@angular/material/dialog'
import { type User } from '@inside-hub-app/customer-portal-b2c-client'
import { CptGoogleTagmanagerService, ResizeService, type SCREEN_SIZE, CustomerPortalSharedModule } from '@inside-hub-app/customer-portal-shared'
import {
  type CustomerPortalConfig, type RevolutionLink
} from '@inside-hub-app/customer-portal-config'
import { KeycloakService } from '@emilfreydigital/keycloak-angular'
import { EfRemoteConfigurationService } from '@inside-hub-app/ef-remote-config'
import { TranslateService } from '@ngx-translate/core'
import { NGXLogger } from 'ngx-logger'
import { DataService } from '../../../../services/data.service'
import { LanguageService } from '../../../../services/language.service'
import { LocalStorageService } from '../../../../services/local-storage.service'
import { type VehicleDTOExtended } from '../../../../services/vehicles.service'
import { MultilanguagePopupComponent } from '../../../basic/multilanguage-popup/multilanguage-popup.component'
import { SharedService } from '../../../../services/shared.service'

import { CommonModule } from '@angular/common'
import { FormsModule, ReactiveFormsModule } from '@angular/forms'
import { RouterModule } from '@angular/router'
import { HubChildTranslateModule } from '@inside-hub-app/hub-translate'
import { NgxSkeletonLoaderModule } from 'ngx-skeleton-loader'
import { NgxGlideModule } from 'ngx-glide'
import { GoogleMapsModule } from '@angular/google-maps'
import { A11yModule } from '@angular/cdk/a11y'
import { TextFieldModule } from '@angular/cdk/text-field'
import { HeaderV2Component } from '../../../basic/header-v2/header-v2.component'
import { NavbarExpandComponent } from '../navbar-expand/navbar-expand.component'
import { NavbarRightComponent } from '../navbar-right/navbar-right.component'
@Component({
  selector: 'customer-portal-app-navbar',
  standalone: true,
  templateUrl: './navbar.component.html',
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    RouterModule,
    CustomerPortalSharedModule,
    HubChildTranslateModule,
    NgxSkeletonLoaderModule,
    NgxGlideModule,
    GoogleMapsModule,
    A11yModule,
    TextFieldModule,
    HeaderV2Component,
    NavbarExpandComponent,
    NavbarRightComponent
  ]
})
export class NavbarComponent implements OnInit, OnDestroy {
  user: User
  vehicles: VehicleDTOExtended[] = []
  vin: string
  loggedIn: boolean
  loading: boolean
  languages: string[]
  initials: string
  primaryLang: string
  show: boolean

  navbarLinks
  currentLanguage: string
  country
  public screenSize: SCREEN_SIZE

  sub = {
    onLangChange: null,
    onUserLoaded: null,
    onUserVehiclesLoaded: null,
    onVehicleVinChange: null,
    onResize: null
  }

  constructor (
    public readonly sharedService: SharedService,
    private readonly dataService: DataService,
    private readonly dialog: MatDialog,
    private readonly languageService: LanguageService,
    private readonly keycloak: KeycloakService,
    private readonly logger: NGXLogger,
    private readonly cptGtmService: CptGoogleTagmanagerService,
    private readonly translate: TranslateService,
    private readonly localStorageService: LocalStorageService,
    public resizeService: ResizeService,
    private readonly cdRef: ChangeDetectorRef,
    private readonly remoteConfigService: EfRemoteConfigurationService<CustomerPortalConfig>
  ) {
    this.screenSize = this.resizeService.screenSize
    this.navbarLinks = this.remoteConfigService.getArrayValues<RevolutionLink[]>(
      'revolution.links.navbar.links'
    )
    this.country = this.remoteConfigService.get('country.code')
  }

  setShow (event): void {
    this.show = event
  }

  getNavbarLink (link: RevolutionLink): string {
    return this.sharedService.getLink(link)
  }

  openLanguageChange (user: User): void {
    const dialogRef = this.dialog.open(MultilanguagePopupComponent, {
      data: user,
      panelClass: 'mat-dialog-cpt'
    })

    dialogRef.afterClosed().subscribe(() => {
      this.load()
    })
  }

  goToLink (url: string): void {
    window.open(url, '_blank')
  }

  ngOnInit (): void {
    this.currentLanguage = this.translate.currentLang

    this.sub.onLangChange = this.translate.onLangChange.subscribe(val => {
      this.currentLanguage = val.lang
    })
    this.loggedIn = this.keycloak.isLoggedIn()

    this.sub.onUserLoaded = this.dataService.onUserLoaded.subscribe(user => {
      this.user = user
      if (user != null) {
        this.loggedIn = true
      }
    })

    this.sub.onUserVehiclesLoaded =
      this.dataService.onUserVehiclesLoaded.subscribe(
        data => {
          this.vehicles = data
          if (this.vin == null && data?.[0] != null) {
            this.vin = data[0].vin
          }
        },
        err => {
          if (err.status === 404) {
            this.vehicles = []
          }
        }
      )
    this.sub.onVehicleVinChange = this.dataService.onVehicleVinChange.subscribe(
      vin => {
        this.vin = vin
      }
    )

    this.sub.onResize = this.resizeService.onResize.subscribe(size => {
      this.screenSize = size
      this.cdRef.detectChanges()
    })
  }

  ngOnDestroy (): void {
    this.sharedService.unsubscribe(this.sub)
  }

  sendGTMNavigationData (label?: string, linkType?: string): void {
    this.cptGtmService.sendNavigationLinkData(
      null,
      'Main Navigation',
      this.sharedService.translateLink(label),
      linkType
    )
  }

  load (): void {
    this.languageService.getLanguages().subscribe(value => {
      this.languages = value
    })
    this.loading = true
    this.dataService.onUserLoaded.subscribe(user => {
      this.user = null
      if (user != null) {
        this.user = user
        this.loading = false
        this.primaryLang =
          this.user.language != null
            ? this.user.language.find(s => s.primary).language
            : 'de'
        this.localStorageService.setLang(this.primaryLang.toLowerCase())
        this.dataService.initialLanguageSetup(this.primaryLang)
      }
    })
  }
}
